/**
 * Enhanced Authentication Service
 * Supports: Email/Password, Google, Passwordless Email, Phone/SMS
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { User, UserRole, Permission, UserProfile } from '@/types';
import { AuthService } from './auth';

const googleProvider = new GoogleAuthProvider();

export class AuthEnhanced extends AuthService {

  // Passwordless Email Link Authentication
  static async sendMagicLink(email: string): Promise<void> {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/verify`,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Save email for later verification
      window.localStorage.setItem('emailForSignIn', email);
    } catch (error: any) {
      throw new Error(`Failed to send magic link: ${error.message}`);
    }
  }

  static async completeMagicLinkSignIn(url: string): Promise<User> {
    try {
      if (!isSignInWithEmailLink(auth, url)) {
        throw new Error('Invalid sign-in link');
      }

      let email = window.localStorage.getItem('emailForSignIn');

      if (!email) {
        // User opened link on different device
        email = window.prompt('Please provide your email for confirmation');
        if (!email) throw new Error('Email required');
      }

      const userCredential = await signInWithEmailLink(auth, email, url);
      window.localStorage.removeItem('emailForSignIn');

      const firebaseUser = userCredential.user;

      // Check if user exists
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (userDoc.exists()) {
        return userDoc.data() as User;
      } else {
        // Create new user
        const user: User = {
          id: firebaseUser.uid,
          role: UserRole.CLIENT,
          permissions: this.getDefaultPermissions(UserRole.CLIENT),
          settings: this.getDefaultSettings(),
          profile: {
            firstName: '',
            lastName: '',
            email: firebaseUser.email || '',
            phone: '',
            avatar: null,
            bio: '',
            company: '',
            licenseNumber: ''
          },
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any,
          lastLogin: new Date() as any,
          status: 'active' as any,
          credits: 5,
          usage: { totalCalls: 0, totalTokens: 0 },
          email: firebaseUser.email || ''
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), user);
        return user;
      }
    } catch (error: any) {
      throw new Error(`Magic link sign-in failed: ${error.message}`);
    }
  }

  // Phone/SMS Authentication
  private static recaptchaVerifier: RecaptchaVerifier | null = null;
  private static confirmationResult: ConfirmationResult | null = null;

  static setupRecaptcha(containerId: string): void {
    if (!this.recaptchaVerifier) {
      this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA solved');
        },
      });
    }
  }

  static async sendPhoneVerificationCode(phoneNumber: string): Promise<void> {
    try {
      if (!this.recaptchaVerifier) {
        throw new Error('reCAPTCHA not initialized. Call setupRecaptcha first.');
      }

      this.confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        this.recaptchaVerifier
      );
    } catch (error: any) {
      // Reset reCAPTCHA on error
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      }
      throw new Error(`Failed to send verification code: ${error.message}`);
    }
  }

  static async verifyPhoneCode(code: string): Promise<User> {
    try {
      if (!this.confirmationResult) {
        throw new Error('No verification in progress');
      }

      const userCredential = await this.confirmationResult.confirm(code);
      const firebaseUser = userCredential.user;

      // Check if user exists
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (userDoc.exists()) {
        return userDoc.data() as User;
      } else {
        // Create new user
        const user: User = {
          id: firebaseUser.uid,
          role: UserRole.CLIENT,
          permissions: this.getDefaultPermissions(UserRole.CLIENT),
          settings: this.getDefaultSettings(),
          profile: {
            firstName: '',
            lastName: '',
            email: firebaseUser.email || '',
            phone: firebaseUser.phoneNumber || '',
            avatar: null,
            bio: '',
            company: '',
            licenseNumber: ''
          },
          createdAt: serverTimestamp() as any,
          updatedAt: serverTimestamp() as any,
          lastLogin: new Date() as any,
          status: 'active' as any,
          credits: 5,
          usage: { totalCalls: 0, totalTokens: 0 },
          email: firebaseUser.email || ''
        };

        await setDoc(doc(db, 'users', firebaseUser.uid), user);
        return user;
      }
    } catch (error: any) {
      throw new Error(`Phone verification failed: ${error.message}`);
    } finally {
      // Clean up
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      }
      this.confirmationResult = null;
    }
  }

  // Quick social sign-in (frictionless)
  static async quickGoogleSignIn(): Promise<User> {
    googleProvider.setCustomParameters({
      prompt: 'select_account'
    });
    return this.signInWithGoogle();
  }
}
